// Water exchange with the paper: absorption, capillary wicking and evaporation.
//
// Absorption moves surface water into the paper's capillary layer at a rate
// set by local absorbency and how far the paper is from saturation. Capillary
// wicking diffuses absorbed water to drier neighbours through the fibres
// (rate varies with fibre density, giving irregular, feathered wet fronts).
// Wicking makes neighbouring dry paper count as wet, which releases pinned
// surface water into it: that is the mechanism behind blooms and backruns
// when water is added to a drying wash.
//
// Evaporation removes surface water faster near the contact line. For a thin
// drop the evaporative flux rises sharply toward a pinned edge (Deegan et al.
// 1997); we model that with an exposure field = fraction of dry cells within
// edgeRadius, so the boost decays smoothly with distance from the edge. The
// loss at the edge is replenished by flow from the interior, which carries
// pigment outward. The paper itself dries slowly.
//
// Capillary replenishment (modelled term): in a real drop the outward flow
// that feeds a pinned edge is driven by Laplace (surface-tension) pressure,
// which our gravity-driven film equation does not resolve at this grid scale.
// We therefore apply it explicitly: the extra evaporation in the edge zone is
// drawn from the wettest neighbour with deeper water, carrying its pigment
// toward the rim, until the film there is thinner than pinRelease (then the
// contact line is free to recede). This is what concentrates pigment into a
// dark drying edge.

import { tileBounds, getScratch } from './region.js';
import { clearFaces, limitOutflow, carryAlongFaces, applyFaces, inRegion } from './FluxTransport.js';
import { TILE_SIZE, PIGMENT_SLOTS } from '../core/PaintState.js';
import { WATER_WET_DEPTH } from './WaterTransport.js';

export const DEFAULT_PAPER_PARAMS = Object.freeze({
  capacity: 0.2, // water the paper holds at saturation
  absorption: 0.004, // per second, times local absorbency
  wicking: 0.25, // capillary diffusivity (cells^2 per second), times local capillarity
  wickMinimum: 0.04, // saturation below which water no longer wicks onward
  evaporation: 0.012, // surface water depth lost per second (open air)
  edgeEvaporation: 5.0, // extra evaporation where the neighbourhood is dry (contact-line zone)
  edgeRadius: 3, // cells: extent of the enhanced-evaporation zone inside the contact line
  paperEvaporation: 0.004, // paper moisture lost per second
  capillaryFeed: 1.0, // fraction of the extra edge evaporation replenished from the interior
  pinRelease: 0.015, // film depth below which the contact line depins and recedes
});

export function stepPaper(state, region, dt, env = {}) {
  const p = { ...DEFAULT_PAPER_PARAMS, ...(env.paper || {}) };
  const { width, height, water, paperSat } = state;
  const { absorbency, capillary } = state.surface;
  const humidity = Math.max(0, Math.min(0.98, env.humidity ?? 0.4));
  const dryness = 1 - humidity;
  // Surfaces dry at different rates (absorbent papers faster, sized boards slower).
  const evapScale = (env.evaporationScale ?? 1) * (state.surface.preset.drying ?? 1) * dryness / 0.6;
  const ledger = state.ledger || (state.ledger = { evaporated: 0, bakedPigment: 0, scraped: 0 });

  const exposure = computeExposure(state, region, p.edgeRadius);
  replenishEdges(state, region, dt * p.evaporation * evapScale * p.edgeEvaporation * p.capillaryFeed, exposure, p.pinRelease);

  // Absorption and evaporation (cell-local).
  for (const t of region.tiles) {
    const [x0, y0, x1, y1] = tileBounds(state, t);
    for (let y = y0; y < y1; y += 1) {
      for (let x = x0; x < x1; x += 1) {
        const i = y * width + x;
        let h = water[i];
        let s = paperSat[i];
        if (h > 0) {
          const room = Math.max(0, 1 - s / p.capacity);
          const soak = Math.min(h, dt * p.absorption * absorbency[i] * room * Math.min(1, h / 0.05 + 0.2));
          h -= soak;
          s += soak;
          const evap = Math.min(h, dt * p.evaporation * evapScale * (1 + p.edgeEvaporation * Math.sqrt(exposure[i])));
          h -= evap;
          ledger.evaporated += evap;
          if (h < 1e-6) {
            ledger.evaporated += h;
            h = 0;
          }
        }
        if (s > 0) {
          const evap = Math.min(s, dt * p.paperEvaporation * evapScale * (h > WATER_WET_DEPTH ? 0.2 : 1));
          s -= evap;
          ledger.evaporated += evap;
        }
        water[i] = h;
        paperSat[i] = s;
      }
    }
  }

  // Capillary wicking: explicit diffusion of saturation, limited for stability.
  const rate = Math.min(0.2, dt * p.wicking);
  const delta = getScratch(state, 'wickDelta', width * height);
  for (const t of region.tiles) {
    const [x0, y0, x1, y1] = tileBounds(state, t);
    for (let y = y0; y < y1; y += 1) for (let x = x0; x < x1; x += 1) delta[y * width + x] = 0;
  }
  for (const t of region.tiles) {
    const [x0, y0, x1, y1] = tileBounds(state, t);
    for (let y = y0; y < y1; y += 1) {
      for (let x = x0; x < x1; x += 1) {
        const i = y * width + x;
        // Faces to cells outside the region are skipped (their delta would be
        // dropped), which keeps wicking conservative.
        if (x + 1 < width && inRegion(state, region, i + 1)) wick(paperSat, capillary, delta, i, i + 1, rate, p.wickMinimum);
        if (y + 1 < height && inRegion(state, region, i + width)) wick(paperSat, capillary, delta, i, i + width, rate, p.wickMinimum);
      }
    }
  }
  for (const t of region.tiles) {
    const [x0, y0, x1, y1] = tileBounds(state, t);
    for (let y = y0; y < y1; y += 1) {
      for (let x = x0; x < x1; x += 1) {
        const i = y * width + x;
        paperSat[i] = Math.max(0, paperSat[i] + delta[i]);
      }
    }
  }
}

// Order-independent: each rim cell's demand is split over all deeper
// neighbours by depth excess and accumulated as face fluxes, then applied with
// the conservative face transport (pigment moves at source concentration).
function replenishEdges(state, region, demandScale, exposure, pinRelease) {
  const { width, height, water } = state;
  const n = width * height;
  const fx = getScratch(state, 'rimFx', n);
  const fy = getScratch(state, 'rimFy', n);
  clearFaces(state, region, fx, fy);
  let any = false;
  for (const t of region.tiles) {
    const [x0, y0, x1, y1] = tileBounds(state, t);
    for (let y = y0; y < y1; y += 1) {
      for (let x = x0; x < x1; x += 1) {
        const i = y * width + x;
        const e = exposure[i];
        const h = water[i];
        if (e <= 0 || h < pinRelease) continue;
        const dl = x > 0 ? Math.max(0, water[i - 1] - h) : 0;
        const dr = x < width - 1 ? Math.max(0, water[i + 1] - h) : 0;
        const du = y > 0 ? Math.max(0, water[i - width] - h) : 0;
        const dd = y < height - 1 ? Math.max(0, water[i + width] - h) : 0;
        const total = dl + dr + du + dd;
        if (total <= 0) continue;
        // Never move more than a stable fraction of the depth difference (no overshoot).
        // De-pinning is gradual as the film thins, which avoids a deposit line
        // every time the front retreats by one cell.
        const t = Math.min(1, (h - pinRelease) / (3 * pinRelease));
        const pinned = t * t * (3 - 2 * t);
        const demand = Math.min(demandScale * Math.sqrt(e) * pinned, 0.1 * total);
        const k = demand / total;
        // Face arrays are signed: positive = towards +x / +y.
        if (dl > 0) fx[i - 1] += k * dl;
        if (dr > 0) fx[i] -= k * dr;
        if (du > 0) fy[i - width] += k * du;
        if (dd > 0) fy[i] -= k * dd;
        any = true;
      }
    }
  }
  if (!any) return;
  limitOutflow(state, region, fx, fy, water);
  for (let k = 0; k < PIGMENT_SLOTS; k += 1) {
    if (state.slots[k]) carryAlongFaces(state, region, fx, fy, water, state.wetPig[k]);
  }
  applyFaces(state, region, fx, fy, water);
}

// Fraction of dry cells in the (2r+1)^2 neighbourhood, by separable box sums.
// Cells outside the processing region are dry by construction.
function computeExposure(state, region, r) {
  const { width, height, water } = state;
  const rows = getScratch(state, 'exposureRows', width * height);
  const out = getScratch(state, 'exposure', width * height);
  const inRegion = (x, y) => {
    const t = Math.floor(y / TILE_SIZE) * state.tilesX + Math.floor(x / TILE_SIZE);
    return region.mask[t] === 1;
  };
  for (const t of region.tiles) {
    const [x0, y0, x1, y1] = tileBounds(state, t);
    for (let y = y0; y < y1; y += 1) {
      for (let x = x0; x < x1; x += 1) {
        let dry = 0;
        for (let dx = -r; dx <= r; dx += 1) {
          const xx = x + dx;
          if (xx < 0 || xx >= width || water[y * width + xx] < WATER_WET_DEPTH) dry += 1;
        }
        rows[y * width + x] = dry;
      }
    }
  }
  const span = (2 * r + 1) * (2 * r + 1);
  for (const t of region.tiles) {
    const [x0, y0, x1, y1] = tileBounds(state, t);
    for (let y = y0; y < y1; y += 1) {
      for (let x = x0; x < x1; x += 1) {
        let dry = 0;
        for (let dy = -r; dy <= r; dy += 1) {
          const yy = y + dy;
          if (yy < 0 || yy >= height || !inRegion(x, yy)) dry += 2 * r + 1;
          else dry += rows[yy * width + x];
        }
        out[y * width + x] = dry / span;
      }
    }
  }
  return out;
}

function wick(sat, capillary, delta, i, j, rate, minimum) {
  const si = sat[i];
  const sj = sat[j];
  const diff = si - sj;
  if (diff === 0) return;
  const source = diff > 0 ? si : sj;
  if (source < minimum) return;
  const conductance = 0.5 * (capillary[i] + capillary[j]);
  const moved = rate * conductance * diff * 0.5;
  delta[i] -= moved;
  delta[j] += moved;
}
